import { useState, useEffect } from 'react';
import { STORY_CHAPTERS } from '@flip7/shared';
import type { ChallengeCompletePayload } from '@flip7/shared';

interface StoryProgress {
  chapter: number;
  level: number;
  stars: number;
  bestScore: number;
  completedAt: string | null;
}

interface StoryScreenProps {
  onStartChallenge: (chapter: number, level: number) => void;
  onBack: () => void;
  challengeResult: ChallengeCompletePayload | null;
}

export function StoryScreen({ onStartChallenge, onBack, challengeResult }: StoryScreenProps) {
  const [progress, setProgress] = useState<StoryProgress[]>([]);
  const [selectedChapter, setSelectedChapter] = useState<number | null>(null);

  useEffect(() => {
    const token = localStorage.getItem('flip7_access_token');
    if (!token) return;
    const apiBase = import.meta.env.VITE_API_URL || '';
    fetch(`${apiBase}/api/challenge/progress`, {
      headers: { 'Authorization': `Bearer ${token}` },
    })
      .then(r => r.json())
      .then(data => {
        if (Array.isArray(data)) setProgress(data);
      })
      .catch(() => {});
  }, [challengeResult]);

  const getProgress = (chapter: number, level: number) =>
    progress.find(p => p.chapter === chapter && p.level === level);

  const isLevelUnlocked = (chapter: number, level: number) => {
    if (chapter === 1 && level === 1) return true;
    if (level === 1) {
      const prevChapter = STORY_CHAPTERS.find(c => c.chapter === chapter - 1);
      if (!prevChapter) return false;
      const lastLevel = prevChapter.levels[prevChapter.levels.length - 1];
      return !!getProgress(prevChapter.chapter, lastLevel.level)?.completedAt;
    }
    return !!getProgress(chapter, level - 1)?.completedAt;
  };

  const totalStars = progress.reduce((sum, p) => sum + p.stars, 0);
  const maxStars = STORY_CHAPTERS.reduce((sum, ch) => sum + ch.levels.length * 3, 0);

  if (selectedChapter !== null) {
    const chapter = STORY_CHAPTERS.find(c => c.chapter === selectedChapter);
    if (!chapter) return null;
    const chapterStars = chapter.levels.reduce((sum, l) => {
      const p = getProgress(chapter.chapter, l.level);
      return sum + (p?.stars ?? 0);
    }, 0);

    return (
      <div className="hero-panel">
        <div className="screen screen--padded-top content-narrow" style={{ padding: 'var(--space-xl) var(--space-lg)' }}>
          <div className="page-header" style={{ marginBottom: 'var(--space-lg)' }}>
            <button className="back-btn" onClick={() => setSelectedChapter(null)}>&larr;</button>
            <div style={{ flex: 1 }}>
              <h2 className="section-header">
                Chapter {chapter.chapter}: {chapter.name}
              </h2>
              <p className="text-xs text-muted" style={{ margin: '2px 0 0' }}>
                {chapterStars}/{chapter.levels.length * 3} stars
              </p>
            </div>
          </div>

          <p className="text-body text-muted">{chapter.description}</p>

          {challengeResult && (
            <div className="ornate-border badge-pop result-panel" style={{
              borderColor: challengeResult.won ? 'var(--gold)' : 'var(--color-bust)',
              background: challengeResult.won ? 'linear-gradient(180deg, #FFF8E1, var(--parchment))' : undefined,
            }}>
              <p className="result-panel__title" style={{
                color: challengeResult.won ? 'var(--gold)' : 'var(--color-bust)',
              }}>
                {challengeResult.won ? 'Victory!' : 'Defeated'}
              </p>
              <div className="result-panel__stars">
                {[0, 1, 2].map(i => (
                  <span key={i} style={{ opacity: i < challengeResult.stars ? 1 : 0.2 }}>
                    {'\u2B50'}
                  </span>
                ))}
              </div>
              <p className="result-panel__detail">
                Score: {challengeResult.score} | Rounds: {challengeResult.roundsPlayed}
                {challengeResult.coinsEarned > 0 && (
                  <span className="text-gold font-bold"> +{challengeResult.coinsEarned} coins</span>
                )}
              </p>
            </div>
          )}

          <div className="flex-col gap-sm">
            {chapter.levels.map(level => {
              const p = getProgress(chapter.chapter, level.level);
              const unlocked = isLevelUnlocked(chapter.chapter, level.level);
              return (
                <button
                  key={level.level}
                  className="ornate-border ornate-border--compact text-left"
                  disabled={!unlocked}
                  onClick={() => unlocked && onStartChallenge(chapter.chapter, level.level)}
                  style={{
                    cursor: unlocked ? 'pointer' : 'default',
                    opacity: unlocked ? 1 : 0.4,
                    border: p?.completedAt ? '2px solid var(--gold)' : undefined,
                  }}
                >
                  <div className="flex-between">
                    <span className="text-display">
                      Level {level.level}
                    </span>
                    <div style={{ display: 'flex', gap: '2px' }}>
                      {[0, 1, 2].map(i => (
                        <span key={i} style={{ fontSize: '0.8rem', opacity: (p?.stars ?? 0) > i ? 1 : 0.15 }}>
                          {'\u2B50'}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className="text-xs text-muted mt-xs">
                    {level.botCount} bot{level.botCount > 1 ? 's' : ''} ({level.botDifficulty}) | Target: {level.targetScore}
                    {level.variant !== 'standard' && (
                      <span className={level.variant === 'vengeance-brutal' ? 'chip chip--brutal' : 'chip chip--vengeance'} style={{ marginLeft: '6px' }}>
                        {level.variant === 'vengeance-brutal' ? 'Brutal' : 'Vengeance'}
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-muted mt-xs">
                    {level.starConditions.map((sc, i) => (
                      <span key={i}>
                        {i > 0 && ' | '}
                        <span style={{ opacity: (p?.stars ?? 0) > i ? 1 : 0.5 }}>
                          {'\u2B50'} {sc.description}
                        </span>
                      </span>
                    ))}
                  </div>
                  {p?.bestScore ? (
                    <p className="text-xs text-gold font-bold mt-xs">
                      Best: {p.bestScore}
                    </p>
                  ) : null}
                </button>
              );
            })}
          </div>

          {chapter.unlockReward && (
            <div className="text-center text-sm text-gold font-bold" style={{ padding: 'var(--space-sm)' }}>
              Complete all levels to unlock: &quot;{chapter.unlockReward.name}&quot; {chapter.unlockReward.type}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Chapter select
  return (
    <div className="hero-panel">
      <div className="screen screen--padded-top content-narrow" style={{ padding: 'var(--space-xl) var(--space-lg)' }}>
        <div className="page-header" style={{ marginBottom: 'var(--space-xl)' }}>
          <button onClick={onBack} className="back-btn">&larr;</button>
          <h1 className="section-header" style={{ flex: 1, fontSize: '1.6rem' }}>Story Mode</h1>
          <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontWeight: 600 }}>
            {totalStars}/{maxStars} stars
          </span>
        </div>

        <div className="flex-col gap-md" style={{ width: '100%', maxWidth: '340px' }}>
          {STORY_CHAPTERS.map(chapter => {
            const chapterStars = chapter.levels.reduce((sum, l) => {
              const p = getProgress(chapter.chapter, l.level);
              return sum + (p?.stars ?? 0);
            }, 0);
            const completed = chapter.levels.filter(l => getProgress(chapter.chapter, l.level)?.completedAt).length;
            const firstLevelUnlocked = isLevelUnlocked(chapter.chapter, 1);

            return (
              <button
                key={chapter.chapter}
                className="ornate-border text-left"
                disabled={!firstLevelUnlocked}
                onClick={() => firstLevelUnlocked && setSelectedChapter(chapter.chapter)}
                style={{
                  cursor: firstLevelUnlocked ? 'pointer' : 'default',
                  opacity: firstLevelUnlocked ? 1 : 0.4,
                  padding: '14px 16px',
                }}
              >
                <div className="flex-between">
                  <span style={{ fontFamily: 'var(--font-display)', fontSize: '1rem', color: 'var(--maroon)' }}>
                    Ch. {chapter.chapter}: {chapter.name}
                  </span>
                  <span className="text-sm text-gold font-bold">
                    {chapterStars}/{chapter.levels.length * 3}
                  </span>
                </div>
                <p className="text-sm text-muted mt-xs">
                  {chapter.description}
                </p>
                <p className="text-xs text-muted" style={{ marginTop: '2px' }}>
                  {completed}/{chapter.levels.length} levels completed
                </p>
              </button>
            );
          })}
        </div>

      </div>
    </div>
  );
}
